import React from 'react';
import { differenceWith, filter } from 'lodash';
import type { GridRowId } from '@mui/x-data-grid-premium';

import { getItemId } from '../Utils';
import useSettings from './useSettings';

export type ItemSelectionEvent = CustomEvent<{ newSelectedItem: any }>;
export type AllItemsSelectionEvent = CustomEvent<{ items: any[] }>;
export type SelectionChangeEvent = CustomEvent<{ items: any[]; newSelectedItemsIds: GridRowId[] }>;

interface SelectionParams {
	mode?: 'single' | 'multiple';
	module: string;
	idField?: string;
	isSelecting?: boolean;
	defaultSelectedItems?: any[];
	parseSelectedItems?: (newSelectedItems: any[]) => any[];
	editCallback?: (newSelectedItems: any[]) => void;
}

interface SelectionType<T = any> {
	selectedItems: T[];
	setSelectedItems: (newSelectedItems: T[]) => void;
}

export interface SelectionProps {
	mode: SelectionParams['mode'];
	selectedItems: SelectionType['selectedItems'];
}

export default function useSelection({
	mode = 'multiple',
	parseSelectedItems,
	editCallback,
	...props
}: SelectionParams) {
	const { settings } = useSettings(props.module);

	const [selectedItems, setSelectedItems] = React.useState<any[]>(props.defaultSelectedItems || []);

	const getModuleItemId = React.useCallback(
		item => getItemId(item, props.idField, settings),
		[props.idField, settings]
	);

	const itemsComparator = React.useCallback(
		(item1, item2) => getModuleItemId(item1) === getModuleItemId(item2),
		[getModuleItemId]
	);

	const handleItemSelection = React.useCallback(
		(event: ItemSelectionEvent) => {
			event.stopPropagation();
			const { newSelectedItem } = event.detail;

			setSelectedItems(state => {
				let newSelectedItems: any[] = mode === 'single' ? [] : [...state];

				const isItemSelected = state.find(selectedItem => itemsComparator(selectedItem, newSelectedItem));

				if (mode === 'single') {
					newSelectedItems = !isItemSelected ? [newSelectedItem] : [];
				}
				if (mode === 'multiple') {
					if (!isItemSelected) newSelectedItems.push(newSelectedItem);
					else
						newSelectedItems = state.filter(
							selectedItem => !itemsComparator(selectedItem, newSelectedItem)
						);
				}

				const parsedNewSelectedItems = parseSelectedItems
					? parseSelectedItems(newSelectedItems)
					: newSelectedItems;

				editCallback?.(parsedNewSelectedItems);
				return [...parsedNewSelectedItems];
			});
		},
		[editCallback, itemsComparator, mode, parseSelectedItems]
	);

	const handleSelectionChange = React.useCallback(
		(event: SelectionChangeEvent) => {
			event.stopPropagation();
			const { items, newSelectedItemsIds } = event.detail;

			setSelectedItems(state => {
				const currentSelectedItems = filter(items, row => {
					const rowId = getModuleItemId(row);
					return rowId ? newSelectedItemsIds.indexOf(rowId) > -1 : false;
				});

				const prevSelectedItems = filter(state, item => {
					const rowId = getModuleItemId(item);
					return rowId ? newSelectedItemsIds.indexOf(rowId) > -1 : false;
				});

				// Removes duplicates
				const newSelectedItems = Array.from(new Set([...prevSelectedItems, ...currentSelectedItems]));

				const parsedNewSelectedItems = parseSelectedItems
					? parseSelectedItems(newSelectedItems)
					: newSelectedItems;

				editCallback?.(parsedNewSelectedItems);
				return [...parsedNewSelectedItems];
			});
		},
		[editCallback, getModuleItemId, parseSelectedItems]
	);

	const handleAllItemsSelection = React.useCallback(
		(event: AllItemsSelectionEvent) => {
			event.stopPropagation();
			const { items } = event.detail;

			setSelectedItems(state => {
				const newSelectedItems = differenceWith(items, state, itemsComparator);

				const parsedNewSelectedItems = parseSelectedItems
					? parseSelectedItems(newSelectedItems)
					: newSelectedItems;

				const newState =
					parsedNewSelectedItems.length === 0
						? differenceWith(state, items, itemsComparator)
						: [...state, ...parsedNewSelectedItems];

				editCallback?.(newState);
				return newState;
			});
		},
		[editCallback, itemsComparator, parseSelectedItems]
	);

	React.useEffect(() => {
		if (props.isSelecting) {
			document.addEventListener('handleItemSelection', handleItemSelection as EventListener);
			document.addEventListener('handleSelectionChange', handleSelectionChange as EventListener);
			if (mode === 'multiple') {
				document.addEventListener('handleAllItemsSelection', handleAllItemsSelection as EventListener);
			}

			return () => {
				document.removeEventListener('handleItemSelection', handleItemSelection as EventListener);
				document.removeEventListener('handleSelectionChange', handleSelectionChange as EventListener);
				if (mode === 'multiple') {
					document.removeEventListener('handleAllItemsSelection', handleAllItemsSelection as EventListener);
				}
			};
		}
	}, [handleAllItemsSelection, handleItemSelection, handleSelectionChange, mode, props.isSelecting]);

	return {
		selectedItems,
		setSelectedItems: newState => {
			setSelectedItems([...newState]);
			editCallback?.(newState);
		}
	} as SelectionType;
}
